import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The theme toggle.
 *
 * Dark mode is a WeTalk extension of Broadsheet, scoped to `[data-theme="dark"]` in
 * `wetalk-tokens.css`, so switching it is one attribute on `<html>` and nothing else — no
 * second stylesheet, no class on every component, and no chance of a screen that styles
 * only one of the two grounds.
 *
 * The choice is remembered in `localStorage`; when nothing has been chosen the operating
 * system's preference decides. That is not a configuration fallback — it is the absence of
 * a *preference*, which has a correct answer (`prefers-color-scheme`) rather than a default.
 */
import { createContext, use, useCallback, useEffect, useState, type ReactNode } from "react";

export const THEME = ["light", "dark"] as const;
export type Theme = (typeof THEME)[number];

const STORAGE_KEY = "wetalk.theme";

interface ThemeState {
  readonly theme: Theme;
  readonly toggle: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function storedTheme(): Theme | null {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    return raw === "light" || raw === "dark" ? raw : null;
  } catch {
    // Private browsing and blocked storage both throw. Not knowing the preference is fine.
    return null;
  }
}

function systemTheme(): Theme {
  return globalThis.matchMedia?.(portalText("shell", "prefers_color_scheme_dark_4d588e32")).matches === true
    ? "dark"
    : "light";
}

export function ThemeProvider({ children }: { readonly children: ReactNode }): ReactNode {
  usePortalTranslation("shell");
  const [theme, setTheme] = useState<Theme>(() => storedTheme() ?? systemTheme());

  useEffect(() => {
    globalThis.document.documentElement.dataset["theme"] = theme;
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Remembering is a nicety; the attribute above is the thing that matters.
    }
  }, [theme]);

  // The attribute is the source of truth for what is PAINTED. Anything that sets it from outside
  // React — an extension, a test harness, a second tab's script — is followed here, so the toggle's
  // label ("Dark mode" / "Light mode") can never disagree with the colours on the screen.
  useEffect(() => {
    const root = globalThis.document.documentElement;
    if (typeof globalThis.MutationObserver !== "function") return undefined;
    const observer = new globalThis.MutationObserver(() => {
      const painted = root.dataset["theme"];
      if (painted === "light" || painted === "dark") setTheme(painted);
    });
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      observer.disconnect();
    };
  }, []);

  const toggle = useCallback(() => {
    setTheme((current) => (current === "light" ? "dark" : "light"));
  }, []);

  return <ThemeContext value={{ theme, toggle }}>{children}</ThemeContext>;
}

export function useTheme(): ThemeState {
  const found = use(ThemeContext);
  if (found === null) throw new Error("No theme in context. Mount <ThemeProvider> at the root.");
  return found;
}
